import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Users, Ruler, Anchor, Calendar, Heart } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { jsonLd } from '../lib/jsonLd';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function YachtDetail() {
  const { id } = useParams();
  const [yacht, setYacht] = useState(null);

  // Set page title dynamically
  usePageTitle(yacht?.name || 'Yacht Details');
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    fetchYacht();
    checkFavorite();
  }, [id]);

  const fetchYacht = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yachts/${id}`);
      setYacht(response.data);
    } catch (error) {
      console.error('Error fetching yacht:', error);
      toast.error('Yacht not found');
    }
  };

  const checkFavorite = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/api/favorites/check/${id}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsFavorited(response.data.is_favorited);
    } catch (error) {
      // User not logged in
    }
  };

  const toggleFavorite = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to save favorites');
        return;
      }

      if (isFavorited) {
        await axios.delete(`${API_URL}/api/favorites/${id}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(
          `${API_URL}/api/favorites/${id}`,
          {},
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/inquiries`, {
        ...formData,
        yacht_id: id,
      });
      toast.success('Inquiry sent! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send inquiry');
    } finally {
      setIsLoading(false);
    }
  };

  if (!yacht) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Schema.org JSON-LD for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": yacht.name,
    "description": yacht.description,
    "image": yacht.images,
    "brand": {
      "@type": "Brand",
      "name": yacht.builder
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": yacht.charter_weekly_usd || yacht.price_usd,
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    },
    "additionalProperty": [
      { "@type": "PropertyValue", "name": "Length", "value": `${yacht.length_ft} ft` },
      { "@type": "PropertyValue", "name": "Year", "value": yacht.year },
      { "@type": "PropertyValue", "name": "Guest Capacity", "value": yacht.guest_capacity },
      { "@type": "PropertyValue", "name": "Cabins", "value": yacht.cabins },
      { "@type": "PropertyValue", "name": "Location", "value": yacht.location },
      { "@type": "PropertyValue", "name": "Type", "value": yacht.yacht_type }
    ]
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(schemaData) }} />
      
      {/* Hero Image */}
      <div className="relative h-[70vh] overflow-hidden" data-testid="yacht-hero">
        <img src={yacht.images[0]} alt={yacht.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050C16] via-transparent to-transparent"></div>
        <Button
          onClick={toggleFavorite}
          className={`absolute top-8 right-8 glass-light hover:bg-white/10 ${
            isFavorited ? 'text-red-500' : 'text-white'
          }`}
          data-testid="favorite-btn"
        >
          <Heart size={20} fill={isFavorited ? 'currentColor' : 'none'} />
        </Button>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs uppercase tracking-[0.2em] text-gold">{yacht.builder}</span>
                <span className="text-white/40">•</span>
                <span className="text-xs uppercase text-white/60">{yacht.listing_type}</span>
              </div>
              <h1
                className="text-5xl md:text-6xl font-medium tracking-tight mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="yacht-name"
              >
                {yacht.name}
              </h1>
              <div className="flex items-center gap-2 text-white/60 mb-6">
                <MapPin size={18} />
                <span className="text-lg">{yacht.location}</span>
              </div>
              <p className="text-3xl font-semibold text-gold" data-testid="yacht-price">
                {yacht.charter_weekly_usd
                  ? `$${yacht.charter_weekly_usd.toLocaleString()} / week`
                  : `$${yacht.price_usd?.toLocaleString()}`}
              </p>
            </div>

            {/* Specs */}
            <div className="glass-light p-8 mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Specifications
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <Ruler size={18} />
                    <span className="text-sm uppercase tracking-wide">Length</span>
                  </div>
                  <p className="text-2xl font-semibold">{yacht.length_ft} ft</p>
                </div>
                {yacht.beam_ft && (
                  <div>
                    <div className="flex items-center gap-2 text-gold mb-2">
                      <Anchor size={18} />
                      <span className="text-sm uppercase tracking-wide">Beam</span>
                    </div>
                    <p className="text-2xl font-semibold">{yacht.beam_ft} ft</p>
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <Users size={18} />
                    <span className="text-sm uppercase tracking-wide">Guests</span>
                  </div>
                  <p className="text-2xl font-semibold">{yacht.guest_capacity}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-gold mb-2">
                    <Calendar size={18} />
                    <span className="text-sm uppercase tracking-wide">Year</span>
                  </div>
                  <p className="text-2xl font-semibold">{yacht.year}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                About This Yacht
              </h2>
              <p className="text-white/70 leading-relaxed text-lg">{yacht.description}</p>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Key Features
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {yacht.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-white/70">
                    <span className="text-gold">•</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Inquiry Form Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass-heavy p-8 sticky top-32">
              <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Inquire About {yacht.name}
              </h3>
              <form onSubmit={handleInquiry} className="space-y-4" data-testid="inquiry-form">
                <div>
                  <Label htmlFor="name" className="text-white/80">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-transparent border-b border-white/20 rounded-none px-0"
                    required
                    data-testid="inquiry-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-white/80">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-transparent border-b border-white/20 rounded-none px-0"
                    required
                    data-testid="inquiry-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-white/80">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-transparent border-b border-white/20 rounded-none px-0"
                    data-testid="inquiry-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-white/80">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-transparent border border-white/20 rounded-none mt-2 min-h-[100px]"
                    required
                    data-testid="inquiry-message"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gold text-[#0A192F] hover:bg-[#E5C55D] py-6 uppercase tracking-widest rounded-none"
                  disabled={isLoading}
                  data-testid="inquiry-submit-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Inquiry'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Yachts */}
      <div className="bg-[#0A192F] py-24 mt-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <h2
            className="text-3xl md:text-4xl font-medium mb-12 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Explore More Yachts
          </h2>
          <div className="text-center">
            <Link to="/listings">
              <Button
                variant="outline"
                className="border-gold text-gold hover:bg-gold hover:text-[#0A192F] px-8 py-6 uppercase tracking-widest rounded-none"
                data-testid="explore-more-btn"
              >
                View All Listings
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
